import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "@mui/material";
import { Eye, Search } from "lucide-react";
import { SortingState, createColumnHelper } from "@tanstack/react-table";

import { ARROW_LEFT, FILTER } from "../../../assets";
import TableComponent from "../../../shared/components/table";

const columnHelper = createColumnHelper<any>();

const createCauseListColumns = (onView: (row: any) => void) => [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("week", {
    header: "Week",
    cell: (info) => (
      <span className="text-[#101828] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("court", {
    header: "Court",
    cell: (info) => (
      <span className="text-[#101828] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("judicialDivision", {
    header: "Judicial Division",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.display({
    id: "cause_list",
    header: "Cause list",
    cell: (info) => (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onView(info.row.original)}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E5E7EB] bg-white text-xs font-medium text-[#111827] shadow-sm"
        >
          <span>View</span>
          <Eye className="w-4 h-4 text-[#98A2B3]" />
        </button>
      </div>
    ),
  }),
];

const CAUSE_LIST_DATA = [
  {
    id: 1,
    week: "Week 20",
    court: "Hon. Justice Michael Ojo",
    judicialDivision: "Enugu Judicial Division",
    date: "May 3, 2024",
  },
  {
    id: 2,
    week: "Week 20",
    court: "Hon. Justice Michael Ojo",
    judicialDivision: "Enugu Judicial Division",
    date: "May 3, 2024",
  },
  {
    id: 3,
    week: "Week 20",
    court: "Hon. Justice Michael Ojo",
    judicialDivision: "Enugu Judicial Division",
    date: "May 3, 2024",
  },
];

function CauseLists() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const tableData = useMemo(
    () =>
      CAUSE_LIST_DATA.map((item, index) => ({
        ...item,
        sn: index + 1,
        name: `${item.court}'s Cause list (${item.week})`,
      })),
    [],
  );

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return tableData;
    const term = searchTerm.toLowerCase();
    return tableData.filter((item) => {
      return (
        item.week.toLowerCase().includes(term) ||
        item.court.toLowerCase().includes(term) ||
        item.judicialDivision.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term)
      );
    });
  }, [searchTerm, tableData]);

  const handleViewCauseList = (row: any) => {
    navigate("/admin/cause-lists/view", {
      state: {
        causeList: row,
      },
    });
  };

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b flex items-center justify-between w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">Cause lists</h1>
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D0D5DD] bg-white text-sm text-[#111827]"
          >
            <img src={FILTER} className="w-[16px] h-[16px]" alt="" />
            <span>All Cases</span>
          </button>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <Link
              to="/admin/overview"
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </Link>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">Cause list</span>
          </div>
        </div>
      </div>

      <div className="flex-1 px-8 py-6">
        <div className="mb-4 max-w-[320px]">
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#D0D5DD] focus:outline-none focus:border-[#00B074] bg-white text-sm placeholder:text-[#98A2B3]"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#98A2B3]" />
          </div>
        </div>

        <div className="bg-white ">
          <TableComponent
            DATA={filteredData}
            // @ts-ignore
            COLUMNS={createCauseListColumns(handleViewCauseList)}
            sorting={sorting}
            setSorting={setSorting}
          />
        </div>
      </div>
    </div>
  );
}

export default CauseLists;
